import { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { PdfJsPages } from "@/components/pdf/PdfJsPages";
import { canEmbedPdfInline } from "@/components/pdf/canEmbedPdfInline";

/** Chromium-PDF-Viewer: linke Seitenleiste ausblenden. */
const PDF_EMBED_HASH = "#navpanes=0";

function asPdfBlob(blob: Blob): Blob {
  return blob.type === "application/pdf" ? blob : new Blob([blob], { type: "application/pdf" });
}

/**
 * PDF im Dokumentfeld.
 * Desktop-Browser zeigen die Blob-URL im iframe. Smartphones lassen das Feld
 * dabei weiß – dort zeichnet PDF.js die Seiten auf ein Canvas.
 */
export function InlinePdfView({
  blob,
  title = "PDF",
  className,
}: {
  blob: Blob;
  title?: string;
  className?: string;
}) {
  const embedInline = useMemo(() => canEmbedPdfInline(), []);
  const pdfBlob = useMemo(() => asPdfBlob(blob), [blob]);
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!embedInline) return;
    const next = URL.createObjectURL(pdfBlob);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [embedInline, pdfBlob]);

  if (!embedInline) {
    return (
      <div className={clsx("min-h-0 overflow-y-auto bg-fill-quaternary/15", className)}>
        <PdfJsPages blob={pdfBlob} title={title} className="px-2 py-3" />
      </div>
    );
  }

  if (!url) return null;

  return (
    <iframe
      title={title}
      src={`${url}${PDF_EMBED_HASH}`}
      className={clsx("w-full border-0 bg-fill-quaternary/15", className)}
    />
  );
}
